import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatStepperModule } from '@angular/material/stepper';
import { AddressRegionsLogicService } from '@app/logic/address-regions';
import { BuildTypeLogicService } from '@app/logic/build-type';
import { IDocumentEntityMappedItem, IDocumentSelectionChangedEvent } from '@app/logic/documents';
import { ILotMappedItem, LotsLogicService } from '@app/logic/lots';
import { LotPermissions } from '@app/core/permissions';
import { ToastService } from '@app/core/services/toast/toast.service';
import { GlobalSettingsLogicService } from '@app/logic/global-settings';
import { GlobalSettingsType } from '@app/shared/enums/global-settings-type/global-setitngs-type';
import { BaseDialogFormViewDirective } from '@app/shared/base-views/base-dialog-form-view.directive';
import { CbDialogContainerModule } from '@app/shared/components/dialog/dialog-container/dialog-container.module';
import {
    IAddressRegionDto,
    IBuildTypeDto,
    IProjectReleaseLotMigrationSourceSelectionGroupDto,
    IProjectReleaseLotMigrationSourceSelectionLotDto,
    LOT_CONTRACT_TYPE_ENUM,
    LOT_TYPE_ENUM,
    LotContractTypeEnumId,
    LotTypeEnumId,
    SYSTEM_AREA_ENUM
} from '@classictechsolutions/hubapi-transpiled-enums';
import { forkJoin, take } from 'rxjs';
import {
    IDocAssignmentRow,
    ILotAdd,
    IData,
    INewLotSummary,
    LOT_MIGRATOR_ERROR_MESSAGE,
    LotMigratorStepIndex
} from './lot-migrator.models';
import { LotMigratorDocumentAssignmentsComponent } from './lot-migrator-document-assignments/lot-migrator-document-assignments.component';
import { LotMigratorDocumentSelectorComponent } from './lot-migrator-document-selector/lot-migrator-document-selector.component';
import { LotMigratorExecutionPreviewDialogComponent } from './lot-migrator-execution-preview-dialog/lot-migrator-execution-preview-dialog.component';
import { LotMigratorLotFormsComponent } from './lot-migrator-lot-forms/lot-migrator-lot-forms.component';
import { LotMigratorReviewComponent } from './lot-migrator-review/lot-migrator-review.component';
import { LotMigratorSourceSelectionComponent } from './lot-migrator-source-selection/lot-migrator-source-selection.component';
import { LotMigratorSourceSummaryComponent } from './lot-migrator-source-summary/lot-migrator-source-summary.component';
import { LotMigratorTargetConfigComponent } from './lot-migrator-target-config/lot-migrator-target-config.component';
import { LotMigratorExportService } from './lot-migrator-export.service';

@Component({
    selector: 'cb-lot-migrator',
    standalone: true,
    imports: [
        CbDialogContainerModule,
        MatButtonModule,
        MatStepperModule,
        LotMigratorSourceSelectionComponent,
        LotMigratorSourceSummaryComponent,
        LotMigratorTargetConfigComponent,
        LotMigratorLotFormsComponent,
        LotMigratorDocumentSelectorComponent,
        LotMigratorDocumentAssignmentsComponent,
        LotMigratorReviewComponent
    ],
    providers: [
        GlobalSettingsLogicService,
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            useValue: { showError: true }
        }
    ],
    templateUrl: './lot-migrator-dialog.component.html',
    styleUrls: ['./lot-migrator-dialog.component.scss']
})
export class LotMigratorDialogComponent
    extends BaseDialogFormViewDirective<any, any, any>
    implements OnInit
{
    public static readonly MIN_WIDTH = '100%';
    public readonly LOT_TYPE_ENUM = LOT_TYPE_ENUM;
    public readonly lotMigratorStepIndex = LotMigratorStepIndex;
    public readonly lotMigratorErrorMessage = LOT_MIGRATOR_ERROR_MESSAGE;
    public contractTypes = LOT_CONTRACT_TYPE_ENUM.toSelectList();
    public readonly lotTypes = LOT_TYPE_ENUM.toSelectList();
    public lots: ILotMappedItem[] = [];
    public migrationLots: IProjectReleaseLotMigrationSourceSelectionGroupDto[] = [];
    public lotMappedItems: ILotMappedItem[] = [];
    public addressRegions: IAddressRegionDto[] = [];
    public buildTypes: IBuildTypeDto[] = [];
    public lotType: LotTypeEnumId = null;
    public migrationLotType: LotTypeEnumId = null;
    public selectedLotsLoaded = false;
    public projectReleaseId: number;
    public maxLotsToMigrate = 0;
    public lotAddMap: ILotAdd[] = [];
    public targetConfigRebuildTrigger = 0;
    public sourceSelectionControl = new FormControl<LotTypeEnumId | null>(
        null,
        Validators.required
    );
    public lotsFormArray: FormArray = new FormArray([]);
    public unavailableLotNumbers = new Set<number>();
    public documentAssignmentsFormArray: FormArray = new FormArray([]);
    public lotSelectedDocuments: { [lotId: number]: IDocumentEntityMappedItem[] } = {};
    public docAssignments: IDocAssignmentRow[] = [];
    public sourceToDestinationLotNumberMap: Record<number, number> = {};
    public systemArea = SYSTEM_AREA_ENUM.MigrateLotsWizard;

    constructor(
        public readonly dialogRef: MatDialogRef<LotMigratorDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public readonly data: IData,
        private readonly toastService: ToastService,
        private readonly lotsLogicService: LotsLogicService,
        public readonly lotPermissions: LotPermissions,
        private readonly addressRegionsLogicService: AddressRegionsLogicService,
        private readonly buildTypeLogicService: BuildTypeLogicService,
        private readonly globalSettingsLogicService: GlobalSettingsLogicService,
        private readonly dialog: MatDialog,
        private readonly exportService: LotMigratorExportService
    ) {
        super(dialogRef, toastService);
        this.lots = [];
        this.migrationLots = data.migrationLots ?? [];
        this.projectReleaseId = data?.projectReleaseId;
        this.filterAllowedContractTypes(data?.allowedLotContractTypes);
        this.syncMigrationLotType();
    }

    public handleSelectedLotsChange(lotIds: number[]): void {
        this.selectedLotsLoaded = false;
        this.sourceSelectionControl.setValue(null);
        this.resetStepState();
        this.mapLotMappedItems(lotIds);
    }

    public handleSelectedLotTypeChange(lotType: LotTypeEnumId): void {
        this.lotType = lotType;
        this.syncSourceSelectionControl();
        this.syncMigrationLotType();
    }

    public handleLotAddMapChange(lotAddMap: ILotAdd[]): void {
        this.lotAddMap = lotAddMap;
    }

    public handleFormsChange(forms: FormArray): void {
        this.lotsFormArray = forms;
    }

    public handleAssignmentsChange(assignments: IDocAssignmentRow[]): void {
        this.docAssignments = assignments;
    }

    public handleAssignmentsFormChange(assignmentsForm: FormArray): void {
        this.documentAssignmentsFormArray = assignmentsForm;
    }

    public handleSourceDestinationMappingChange(
        sourceToDestinationMap: Record<number, number>
    ): void {
        this.sourceToDestinationLotNumberMap = sourceToDestinationMap;
    }

    public handleDocumentSelectionUpdated(selectionEvent: IDocumentSelectionChangedEvent): void {
        const lotId = selectionEvent?.entityId;
        if (!Number.isFinite(lotId)) {
            return;
        }

        this.lotSelectedDocuments = {
            ...this.lotSelectedDocuments,
            [lotId]: [...selectionEvent?.selectedDocuments]
        };
    }

    public get lotNumbers(): number[] {
        return this.lotsFormArray.getRawValue().map(x => x.LotNumber);
    }

    public get hasSelectedDocuments(): boolean {
        return Object.values(this.lotSelectedDocuments).some(documents => documents.length > 0);
    }

    public get cancelledLots(): ILotMappedItem[] {
        return this.lots;
    }

    public get newLotSummaries(): INewLotSummary[] {
        return this.lotsFormArray.getRawValue().map(lot => ({
            lotNumber: lot.LotNumber,
            lotType: lot.LotType
        }));
    }

    public hasInvalidLotDetails(selectedIndex: number): boolean {
        return (
            selectedIndex === this.lotMigratorStepIndex.ReviewLotDetails &&
            this.lotsFormArray.invalid
        );
    }

    public hasInvalidDocumentAssignments(selectedIndex: number): boolean {
        return (
            selectedIndex === this.lotMigratorStepIndex.AssignDocumentDestinations &&
            this.hasSelectedDocuments &&
            this.documentAssignmentsFormArray.invalid
        );
    }

    public openExecutionPanel(): void {
        if (this.lotsFormArray.invalid) {
            this.lotsFormArray.markAllAsTouched();
            this.toastService.showToast(
                'Please fix validation errors in the lot details before confirming.'
            );
            return;
        }

        if (this.hasSelectedDocuments && this.documentAssignmentsFormArray.invalid) {
            this.documentAssignmentsFormArray.markAllAsTouched();
            this.toastService.showToast(
                'Please assign a destination lot for each selected document before confirming.'
            );
            return;
        }

        const file = this.exportService.buildImportFile('lot-import.csv', this.lotsFormArray);

        this.dialog
            .open(LotMigratorExecutionPreviewDialogComponent, {
                data: {
                    lots: this.lots,
                    newLotSummaries: this.newLotSummaries,
                    docAssignments: this.docAssignments,
                    sourceToDestinationLotNumberMap: this.sourceToDestinationLotNumberMap,
                    importFile: file,
                    projectReleaseId: this.projectReleaseId,
                    projectReleaseStageMappedItem: this.data.projectReleaseStageMappedItem
                },
                disableClose: true,
                minWidth: '1080px',
                width: 'min(1080px, 96vw)'
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(result => {
                if (result) {
                    this.toastService.showToast('Lot migration completed.');
                    this.dialogRef.close(true);
                }
            });
    }

    public ngOnInit(): void {
        this.loadOptions();
        this.loadMaxLotsToMigrate();
    }

    private loadOptions(): void {
        forkJoin({
            regions: this.addressRegionsLogicService.$getList().pipe(take(1)),
            types: this.buildTypeLogicService.$getList().pipe(take(1))
        }).subscribe(
            ({ regions, types }: { regions: IAddressRegionDto[]; types: IBuildTypeDto[] }) => {
                this.addressRegions = regions;
                this.buildTypes = types;
            }
        );
    }

    private loadMaxLotsToMigrate(): void {
        this.globalSettingsLogicService
            .$getItem(GlobalSettingsType.MaxLotsToMigrate)
            .pipe(take(1))
            .subscribe(dto => {
                const value = Number(dto?.value);
                if (Number.isFinite(value) && value >= 0) {
                    this.maxLotsToMigrate = value;
                }
            });
    }

    private mapLotMappedItems(lotIds: number[]): void {
        if (lotIds.length < 1) {
            this.lots = [];
            this.lotMappedItems = [];
            this.rebuildUnavailableLotNumbers();
            this.selectedLotsLoaded = false;
            this.syncSourceSelectionControl();
            return;
        }

        forkJoin(
            lotIds.map(lotId => this.lotsLogicService.$getMappedItem(lotId).pipe(take(1)))
        ).subscribe((mappedLots: ILotMappedItem[]) => {
            const sortedLots = mappedLots.sort((a, b) => a.lotNumber - b.lotNumber);
            this.lots = sortedLots;
            this.lotMappedItems = sortedLots;
            this.rebuildUnavailableLotNumbers();
            this.selectedLotsLoaded = true;
            this.syncSourceSelectionControl();
        });
    }

    private resetStepState(): void {
        this.lotAddMap = [];
        this.targetConfigRebuildTrigger++;
        this.lotsFormArray = new FormArray([]);
        this.documentAssignmentsFormArray = new FormArray([]);
        this.lotSelectedDocuments = {};
        this.docAssignments = [];
        this.sourceToDestinationLotNumberMap = {};
    }

    private syncMigrationLotType(): void {
        this.migrationLotType =
            this.lotType === LotTypeEnumId.TerracedBlock
                ? LotTypeEnumId.Standalone
                : this.lotType === LotTypeEnumId.Standalone
                  ? LotTypeEnumId.TerracedBlock
                  : null;
    }

    private syncSourceSelectionControl(): void {
        const value = this.selectedLotsLoaded && this.lots.length > 0 ? this.lotType : null;
        this.sourceSelectionControl.setValue(value);
    }

    private rebuildUnavailableLotNumbers(): void {
        const selectedLotNumbers = new Set(this.lotMappedItems.map(lot => lot.lotNumber));
        const unavailableLotNumbers = new Set<number>();

        this.migrationLots.forEach(group => {
            group.lots.forEach(row => {
                this.addUnavailableLotNumber(row.lot, selectedLotNumbers, unavailableLotNumbers);
                row.childLots.forEach(childLot =>
                    this.addUnavailableLotNumber(
                        childLot,
                        selectedLotNumbers,
                        unavailableLotNumbers
                    )
                );
            });
        });

        this.unavailableLotNumbers = unavailableLotNumbers;
    }

    private addUnavailableLotNumber(
        lot: IProjectReleaseLotMigrationSourceSelectionLotDto,
        selectedLotNumbers: Set<number>,
        unavailableLotNumbers: Set<number>
    ): void {
        if (selectedLotNumbers.has(lot.lotNumber)) {
            return;
        }

        unavailableLotNumbers.add(lot.lotNumber);
    }

    private filterAllowedContractTypes(allowedContractTypes: LotContractTypeEnumId[] = []): void {
        const allowedSet = new Set(allowedContractTypes.map(type => Number(type)));
        if (allowedSet.size < 1) {
            this.contractTypes = [];
            return;
        }

        const filtered = this.contractTypes.filter(type => allowedSet.has(Number(type.id)));
        if (filtered.length > 0) {
            this.contractTypes = filtered;
        }
    }
}
